import React from 'react';
import InputColor from 'react-input-color';
import { TextField, Toggle } from '@cimpress/react-components';
import { getHexColor } from 'src/util/misc';
import { GLOW_RADIUS_MAX, GLOW_RADIUS_MIN } from 'src/config/constants';
import { Content } from '@rendering/components';
import { GlowEffect } from 'src/config/effects';

interface Props {
    id: string;
    effect: GlowEffect;
    updateEffectProperty: (data: { id: string; property: string; value: any }) => void;
}

const rgbToHex = (r: number, g: number, b: number): string => {
    const toHex = (n: number) => {
        const hex = Math.max(0, Math.min(255, Math.round(n))).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const Glow: React.FC<Props> = ({ id, effect, updateEffectProperty }) => {
    const handleColorChange = (evt: { hex: string }) => {
        const hex = getHexColor(evt.hex);
        // Parse hex to RGB
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (result) {
            const r = parseInt(result[1], 16);
            const g = parseInt(result[2], 16);
            const b = parseInt(result[3], 16);
            updateEffectProperty({ id, property: 'r', value: r });
            updateEffectProperty({ id, property: 'g', value: g });
            updateEffectProperty({ id, property: 'b', value: b });
        }
    };

    const handleRadiusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = parseFloat(e.target.value);
        if (!isNaN(value)) {
            value = Math.max(GLOW_RADIUS_MIN, Math.min(GLOW_RADIUS_MAX, value));
            updateEffectProperty({ id, property: 'radius', value });
        }
    };

    const handleLightChange = () => {
        updateEffectProperty({ id, property: 'light', value: !effect.light });
    };

    const colorHex = rgbToHex(effect.r, effect.g, effect.b);

    return (
        <Content className='glow-settings' isFlex={true} flexDirection='column' gap='8px'>
            <h3>Glow Settings</h3>

            <span>Glow Color</span>
            <InputColor initialValue={colorHex} onChange={handleColorChange} placement='top' />
            <span>Glow Radius</span>
            <TextField
                label='Glow Radius (1-100)'
                type='number'
                value={effect.radius}
                onChange={handleRadiusChange}
                className='glow-radius-input'
                data-testid='glow-radius-input'
                min={GLOW_RADIUS_MIN}
                max={GLOW_RADIUS_MAX}
            />
            <span>Light On/Off</span>
            <Toggle className='glow-light-checkbox' on={effect.light} onClick={handleLightChange} />
        </Content>
    );
};

export default React.memo(Glow);
